import { Injectable, signal } from '@angular/core';
import { CombinationsFormGroup, ItemCombinationsFields, ItemCombinationValuesFields, ItemsCombinationValuesFormArray, ItemsFormWithCustomVariations, ItemsVariationsWithCustomCombinationsValue, ItemVariationsFields, ItemVariationsFormGroups, ItemVariationsValidators, VariationsFormGroup } from '../item-variations.model';
import { ItemsCombinations, ItemsCombinationValues } from 'tilby-models';
import { CustomFormArray, CustomFormControl, CustomFormGroup } from '@tilby/tilby-ui-lib/components/tilby-magic-form';
import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { v4 as generateUuid } from 'uuid';


const RequireMatch = <T extends { id: number, value: string }>(controlList: T[]): ValidatorFn => {
    return (control: AbstractControl): ValidationErrors|null => {
        if(typeof control.value === 'object') return null;
        const selection: any = control.value? control.value.toLowerCase().trim() : '';
        if (controlList.find(({value}) => value?.toLowerCase() === selection) || selection === '' ) {
            return null;
        }
        return {matchRequired: true};
    }
}
const RequireSelection = <T extends { id: number, value: string }>(controlList: T[]): ValidatorFn => {
    return (control: AbstractControl): ValidationErrors|null => {
        if(typeof control.value === 'object') return null;
        const selection: any = control.value? control.value : null;
        if (controlList.find(({id}) => id == selection?.id) || selection === null ) {
            return null;
        }
        return {selectionRequired: true};
    }
}

const AtLeastOneVariationRequired = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const formArray = control as CustomFormArray<any>;
    if (!formArray || !formArray.controls.length) return null;

    // Controllo se esiste ALMENO una combinazione che non ha nessun valore selezionato
    const hasInvalidCombination = formArray.controls.some((combinationGroup) => {
      const combinationValues = combinationGroup.get('combination_values') as CustomFormArray<any>;
      if (!combinationValues || !combinationValues.controls.length) return true;

      // Controllo se in questa specifica combinazione c'è almeno un variation_value valorizzata
      const hasAtLeastOneValue = combinationValues.controls.some((cv) => {
        const val = cv.get('variation_value')?.value;
        return val !== null && val !== undefined && (typeof val === 'object' ? !!val.value : !!val);
      });

      // Se non ha almeno un valore, la combinazione è invalida
      return !hasAtLeastOneValue; 
    });

    return hasInvalidCombination ? { atLeastOneVariationRequired: true } : null;
  };
};

@Injectable()
export class CombinationsFormService {

  private combinationsForm!: ItemVariationsFormGroups;

  initialCombinations: Array<ItemsCombinations> = [];

  get form() {
    return this.combinationsForm;
  }
      
  validators: ItemVariationsValidators = {
    nameMaxLength: 255,
    nameVariantValueMaxLength: 255,
    minValueMinQt: 0,
    minValueMaxQt: 0,
    minPriceCombination: 0,
    skuMaxLength: 64,
    nameAddedVariantMaxLength: 255,

    // --------
    provMinLength: 0,
    provMaxLength: 255,
    provMaxLengthArray: new Array(10).fill(2)
  };

  updateVariationValueNameListener = signal<number>(0);

  triggerVariationValueNameListener() {
    this.updateVariationValueNameListener.update(v => v + 1);
  }


  createForm(combinations: Array<ItemsCombinations> = [], variationData: VariationsFormGroup) {
    this.initialCombinations = combinations || [];

    this.combinationsForm = new CustomFormGroup<ItemsFormWithCustomVariations>({
        combinations: new CustomFormArray<CustomFormGroup<ItemsVariationsWithCustomCombinationsValue>>(
          [],
          { validators: [AtLeastOneVariationRequired()] }
        )
    });
    
    if(combinations?.length) this.initCombinations(combinations, variationData);
    this.updateNameComposition();

    return this.combinationsForm;
  }

  resetFormValues(originalCombinations: ItemsCombinations[] = [], originalVariations: VariationsFormGroup) {
    if (!this.combinationsForm) return;

    this.combinationsArray.clear();

    if (originalCombinations && originalCombinations.length > 0) {
      this.initCombinations(originalCombinations, originalVariations);
      this.updateNameComposition();
    }

    this.updateVariationValueNameListener.update(v => v + 1);
  }
  
  addNewCombination(variationsData: VariationsFormGroup, combination?: ItemsCombinations) {
    this.combinationsArray?.push(
      new CustomFormGroup<ItemsVariationsWithCustomCombinationsValue>(
        {
          id: new CustomFormControl(
            combination?.id || 0
          ),
          price1: new CustomFormControl(
            combination?.price1 || null
          ),
          price2: new CustomFormControl(
            combination?.price2 || null
          ),
          price3: new CustomFormControl(
            combination?.price3 || null
          ),
          price4: new CustomFormControl(
            combination?.price4 || null
          ),
          price5: new CustomFormControl(
            combination?.price5 || null
          ),
          price6: new CustomFormControl(
            null
          ),
          price7: new CustomFormControl(
            null
          ),
          price8: new CustomFormControl(
            null
          ),
          price9: new CustomFormControl(
            null
          ),
          price10: new CustomFormControl(
            null
          ),
          sku: new CustomFormControl(
            combination?.sku || null
          ),
          barcodes: new CustomFormControl( 
            combination?.barcodes || []
          ),
          combination_values: new CustomFormArray<CustomFormGroup<ItemsCombinationValuesFormArray>>(
            []
          ),
          combination_name: new CustomFormControl(
            null
          ),
          name_added_variant: new CustomFormControl(
            null,
            { validators: [Validators.maxLength(this.validators.nameVariantValueMaxLength)] }, 
          ),
          expanded: new CustomFormControl(true)
        }
      )
    )
    const index = this.combinationsArray?.length || 0;
    this.addCombinationValues(index? index - 1: 0, variationsData, combination?.combination_values);
  }

  addCombinationValues(combinationIndex: number, variationsData: VariationsFormGroup, combinationValues?: ItemsCombinationValues[]) {
    variationsData?.controls?.forEach((variationControl, index) => {
        let allowedValues = variationControl.get('variation_values')?.value || [];
        
        const rawValue = combinationValues?.at(index)?.variation_value;

        const initialValue = (typeof rawValue === 'string' 
            ? allowedValues.find(v => v.value === rawValue) 
            : rawValue) ?? null;

        const variation = variationControl.value;
        this.getCombinationValues(combinationIndex)?.push(
            new CustomFormGroup<ItemsCombinationValuesFormArray>(
                {
                  variation_name: new CustomFormControl(
                    variation.name
                  ),
                  variation_value: new CustomFormControl(
                      initialValue,
                      { validators: [RequireMatch([...allowedValues].map((v: any) => ({id: v.id, value: v.value})) || []),
                        RequireSelection([...allowedValues].map((v: any) => ({id: v.id, value: v.value})) || [])
                    ]},
                  ),
                  variation_id: new CustomFormControl(
                    variation.id
                  ),
                  tmp_variation_id: new CustomFormControl(
                    variation.tmp_id
                  ),
                  tmp_variation_value_id: new CustomFormControl(
                    generateUuid()
                  ),
                  variation_value_list: new CustomFormControl(
                    variationControl.get('variation_values')?.value || []
                  )
                }
            )
        )
    })
  }


  removeCombination(combinationIndex: number) {
    this.combinationsArray?.removeAt(combinationIndex);
  }

  removeVariationCombination(combinationIndex: number, variationIndex: number) {
    this.getCombinationValues(combinationIndex)?.removeAt(variationIndex);
  }

  removeVariationValueCombination(combinationIndex: number, variationIndex: number, variationValueIndex: number) {
    this.getVariationValueListCombination(combinationIndex, variationIndex)?.value.splice(variationValueIndex, 1);
  }

  removeAllCombinations() {
    for (let i = (this.combinationsArray?.value.length || 0) - 1; i >= 0; i--) {
        this.removeCombination(i);
    }
  }

  updateVariationValueValidators(variationIndex: number, variationValues: any[]) {
    this.combinationsArray?.value.forEach((combination, combinationIndex) => {
        this.getCombinationValues(combinationIndex)?.value.forEach((combinationValue, combinationValueIndex) => {
            const newValidators = [
                RequireMatch(variationValues.map(({tmp_id, value}) => ({id: tmp_id, value})) || []),
                RequireSelection(variationValues.map(({tmp_id, value}) => ({id: tmp_id, value})) || [])
            ]
            this.getVariationValueCombination(combinationIndex, variationIndex)?.setValidators(newValidators);
        })
    })
  }

  updateAddedVariationInCombination(addedVariation: any) {
    this.combinationsArray?.value.forEach((combination, combinationIndex) => {
        this.getCombinationValues(combinationIndex)?.push(
            new CustomFormGroup<ItemsCombinationValuesFormArray>(
                {
                    variation_name: new CustomFormControl(
                        addedVariation.name
                    ),
                    variation_value: new CustomFormControl(
                        null
                    ),
                    variation_id: new CustomFormControl(
                        addedVariation.id
                    ),
                    tmp_variation_id: new CustomFormControl(
                        addedVariation.tmp_id
                    ),
                    tmp_variation_value_id: new CustomFormControl(
                        generateUuid()
                    ),
                    variation_value_list: new CustomFormControl(
                        []
                    )
                }
            )
        )
    })
  }
  
  updateVariationNameInCombination(variationsData: VariationsFormGroup) {
    this.combinationsArray?.value.forEach((combination, combinationIndex) => {
        this.getCombinationValues(combinationIndex)?.value.forEach((combinationValue, combinationValueIndex) => {
            this.getVariationNameCombination(combinationIndex, combinationValueIndex)?.setValue(
                variationsData.at(combinationValueIndex)?.get('name')?.value, {emitEvent: false}
            )
        })
    })
  }

  updateVariationValueInCombination(variationsData: VariationsFormGroup) {
    this.combinationsArray?.value.forEach((combination, combinationIndex) => {
        this.getCombinationValues(combinationIndex)?.value.forEach((combinationValue, variationIndex) => {
            // Aggiorno la lista di opzioni disponibili per questo campo mat-autocomplete
            const newAvailableOptions = variationsData.at(variationIndex).get('variation_values')?.value || [];

            this.getVariationValueListCombination(combinationIndex, variationIndex)?.setValue(
                newAvailableOptions, { emitEvent: false } 
            );

            // Sincronizzo il valore SELEZIONATO nel campo input dell'autocomplete (se coinvolto)
            const formControlForInput = this.getVariationValueCombination(combinationIndex, variationIndex);
            const currentSelectedOption = formControlForInput?.value; // L'oggetto opzione {id: ..., value: 'vecchio testo'}

            if (formControlForInput && currentSelectedOption && currentSelectedOption.tmp_id) {
                // Cerco l'opzione aggiornata nella lista delle NUOVE opzioni disponibili
                const updatedOptionFromAvailable = newAvailableOptions?.find((opt: any) => opt.tmp_id === currentSelectedOption.tmp_id) as any;

                if (updatedOptionFromAvailable) {
                    // Se l'ID corrisponde e il valore visualizzato è cambiato
                    if (updatedOptionFromAvailable.value !== currentSelectedOption.value) {
                        formControlForInput.setValue(updatedOptionFromAvailable, { emitEvent: false });
                    }
                } else {
                    // L'opzione precedentemente selezionata non esiste più nella nuova lista
                    formControlForInput.setValue(null, { emitEvent: false });
                }
            }
        })
    })
  }

  initCombinations(combinations: ItemsCombinations[], variationsData: VariationsFormGroup) {
    combinations.forEach(combination => {
      this.addNewCombination(variationsData, combination);
    })
  }

  updateNameComposition() {
    this.combinationsArray?.value.forEach((combination, combinationIndex) => {
        this.updateOneNameComposition(combinationIndex);
    })
  }

  updateOneNameComposition(combinationIndex: number) {
    let nameCombination = '';
    this.getCombinationValues(combinationIndex)?.value.forEach((combinationValue, combinationValueIndex) => {
        if(nameCombination) nameCombination += ", ";
        if(this.getVariationNameCombination(combinationIndex, combinationValueIndex)?.value) {
            nameCombination += this.getVariationNameCombination(combinationIndex, combinationValueIndex)?.value;
        }
        nameCombination += ": ";
        if(this.getVariationValueCombination(combinationIndex, combinationValueIndex)?.value) {
          const variationValue = typeof this.getVariationValueCombination(combinationIndex, combinationValueIndex)?.value === 'string' ?
            this.getVariationValueCombination(combinationIndex, combinationValueIndex)?.value: this.getVariationValueCombination(combinationIndex, combinationValueIndex)?.value.value;
          nameCombination += "<strong>" + variationValue + "</strong>";
        } else {
            nameCombination += "<strong>-</strong>"
        }
    })
    this.getNameCombination(combinationIndex)?.setValue(nameCombination, {emitEvent: false});
  }

  // Restituisce gli indici delle combinazioni che usano la variante corrispondente all'indice variationIndex
  getIndicesUsingVariation(variationIndex: number): number[] {
    const indices: number[] = [];
    this.combinationsArray?.value.forEach((combination: any, index: number) => {
      const combinationValue = this.getCombinationValues(index)?.at(variationIndex)?.value;
      if(combinationValue?.variation_value) {
        indices.push(index);
      }
    });
    return indices;
  }

  // Metodo per rimuovere le combinazioni dato un array di indici
  removeCombinationsByIndices(indices: number[]) {
    indices.sort((a, b) => b - a).forEach(index => {
      this.removeCombination(index);
    });
  }

  removeVariationColumnFromAllCombinations(variationIndex: number) {
    this.combinationsArray?.controls.forEach((combination, combinationIndex)=> {
      this.removeVariationCombination(combinationIndex, variationIndex);
    })
  }

  /**
   * Riordina la sequenza di varianti nelle combinazioni in base al nuovo ordine delle varianti
   * @param sortedVariations L'array delle varienti già riordinato
   */
  syncCombinationsWithNewVariationOrder(sortedVariations: ItemVariationsFields[]) {
    if (!this.combinationsArray?.length) return;

    this.combinationsArray.controls.forEach((combinationGroup, combinationIndex) => {
      const combinationValuesArray = this.getCombinationValues(combinationIndex);

      // Mappa: TmpID della Variante -> Il FormGrooup corrispondente nella combinazione attuale
      const valueMap = new Map<string, CustomFormGroup<ItemsCombinationValuesFormArray>>();

      combinationValuesArray?.controls.forEach((control, index) => {
        const tmpVarId = this.getTmpVariationIdCombination(combinationIndex, index)?.value;
        if (tmpVarId) {
          valueMap.set(tmpVarId, control);
        }
      });

      // Svuoto l'array attuale (i controlli sono salvati nella map)
      while (combinationValuesArray?.length !== 0) {
        combinationValuesArray?.removeAt(0);
      }

      // Ricostruzione dell'array nell'ordine corretto
      sortedVariations.forEach((variation) => {
        const tmpId = variation.tmp_id || "";
        const matchingControl = valueMap.get(tmpId);

        if (matchingControl) {
          combinationValuesArray.push(matchingControl);
        }
      });
    })
  }


  /**
   * Genera tutte le combinazioni possibili partendo dai dati delle variazioni
   */
  generateAllCombinations(variationsData: VariationsFormGroup, defaultPrices: any) {
    // Elimina eventuali combinazioni già create
    this.removeAllCombinations();

    // Genera le combinazioni
    this.generateCombinationsRecursive([...variationsData.value], [], defaultPrices, variationsData);
    
    // Aggiorna nomi combinazioni
    this.updateNameComposition();
  }

  private generateCombinationsRecursive(
    variations: any[], 
    currentCombination: any[], 
    prices: any,
    variationsData: VariationsFormGroup
  ) {
    // Caso Base: Nessuna variazione rimasta -> Crea la combinazione
    if (!variations.length) {
      this.addNewCombination(
        variationsData,
        { 
          combination_values: currentCombination,
          ...prices
        }
      );
      return;
    }

    // Caso Ricorsivo
    const currentVariation = variations[0];
    const remainingVariations = variations.slice(1);
    const values = currentVariation.variation_values || [];

    for (let i = 0; i < values.length; i++) {
      const newCombination = [...currentCombination, {
          variation_value: values[i]
      }];
      this.generateCombinationsRecursive(remainingVariations, newCombination, prices, variationsData);
    }
  }


  
  getCombinationsFields(): Partial<ItemCombinationsFields>[] {
    // Copio l'oggetto di destinazione per non mutare l'originale
    let updatedCombinations = [...this.combinationsArray?.value || []] as Partial<ItemCombinationsFields>[]; 

    // Recupero tutte le chiavi (nomi dei campi) presenti nel form
    const combinationsFieldsToMap = Object.keys(this.combinationsForm?.controls || {});

    // Ciclo su tutte le chiavi trovate nel form
    combinationsFieldsToMap.forEach(key => {
      const control = this.combinationsForm.get(key);

      if(control) {
        let value = control.value;

        if (key === 'combinations' && Array.isArray(value)) {
          // Creazione mappa <ID: combinazione> per le combinazioni dell'item già salvate 
          const initialCombsMap = new Map(this.initialCombinations.map(v => [v.id, v]));
      
          updatedCombinations = this.combinationsArray.value.map((combination: any) => {
              // Trova la combinazione già salvata per l'item (o oggetto vuoto se non trovato)
              const initial = combination.id? initialCombsMap.get(combination.id): undefined;
      
              // Creazione mappa <ID: barcode> per i barcode della combinazione già salvati
              // const initialBarcodesMap = new Map((initial?.barcodes || []).map(bar => [bar.id, bar]));
      
              const combination_values_list = combination.combination_values as ItemCombinationValuesFields[];
      
              // Gestione COMBINATION VALUES
              const combination_values = combination_values_list
                  // considero solo le combinazioni valide (quelle compilate)
                  ?.filter((item: ItemCombinationValuesFields) => item.variation_name && item.variation_value) 
                  // costruisco l'oggetto
                  .map(({variation_name, variation_value}: any) => ({
                      variation_name,
                      variation_value: variation_value.value
              }));
      
              const rawCombination: ItemsCombinations = {
                  ...(initial || {}),
                  price1: combination.price1 || 0,
                  price2: combination.price2,
                  price3: combination.price3,
                  price4: combination.price4,
                  price5: combination.price5,
                  sku: combination.sku,
                  combination_values,
                  barcodes: combination.barcodes
              };
      
              return rawCombination; 
          });
        }
      }
    })

    return updatedCombinations;
  }


  get combinationsArray() {
    return this.combinationsForm?.controls.combinations as CombinationsFormGroup;
  }

  getIdCombination(index: number) {
    return this.combinationsArray?.at(index).controls.id;
  }

  getPrice1Combination(index: number) {
    return this.combinationsArray?.at(index).controls.price1;
  }

  getPrice2Combination(index: number) {
    return this.combinationsArray?.at(index).controls.price2;
  }

  getPrice3Combination(index: number) {
    return this.combinationsArray?.at(index).controls.price3;
  }

  getPrice4Combination(index: number) {
    return this.combinationsArray?.at(index).controls.price4;
  }

  getPrice5Combination(index: number) {
    return this.combinationsArray?.at(index).controls.price5;
  }

  getPrice6Combination(index: number) {
    return this.combinationsArray?.at(index).controls.price5;
  }

  getPrice7Combination(index: number) {
    return this.combinationsArray?.at(index).controls.price5;
  }

  getPrice8Combination(index: number) {
    return this.combinationsArray?.at(index).controls.price5;
  }

  getPrice9Combination(index: number) {
    return this.combinationsArray?.at(index).controls.price5;
  }

  getPrice10Combination(index: number) {
    return this.combinationsArray?.at(index).controls.price5;
  }

  getSkuCombination(index: number) {
    return this.combinationsArray?.at(index).controls.sku;
  }

  getBarcodesCombination(index: number) {
    return this.combinationsArray?.at(index).controls.barcodes;
  }

  getNameCombination(index: number) {
    return this.combinationsArray?.at(index).controls.combination_name;
  }

  getNameAddedVariantCombination(index: number) {
    return this.combinationsArray?.at(index).controls.name_added_variant;
  }

  getExpandedCombination(index: number) {
    return this.combinationsArray?.at(index).controls.expanded;
  }

  getCombinationValues(index: number) {
    return this.combinationsArray?.at(index).controls.combination_values;
  }

  getVariationNameCombination(index: number, combinationValueIndex: number) {
    return this.getCombinationValues(index)?.at(combinationValueIndex).controls.variation_name;
  }

  getVariationValueCombination(index: number, combinationValueIndex: number) {
    return this.getCombinationValues(index)?.at(combinationValueIndex)?.controls.variation_value;
  }

  getVariationIdCombination(index: number, combinationValueIndex: number) {
    return this.getCombinationValues(index)?.at(combinationValueIndex)?.controls.variation_id;
  }

  getTmpVariationIdCombination(index: number, combinationValueIndex: number) {
    return this.getCombinationValues(index)?.at(combinationValueIndex)?.controls.tmp_variation_id;
  }

  getTmpVariationValueIdCombination(index: number, combinationValueIndex: number) {
    return this.getCombinationValues(index)?.at(combinationValueIndex)?.controls.tmp_variation_value_id;
  }

  getVariationValueListCombination(index: number, combinationValueIndex: number) {
    return this.getCombinationValues(index)?.at(combinationValueIndex).controls.variation_value_list;
  }
}
